import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ExpandSearchGroup } from '@digilize/shared/definitions/src';

@Component({
  selector: 'lib-multi-select-chips-expand-search',
  templateUrl: './multi-select-chips-expand-search.component.html',
  styleUrls: ['./multi-select-chips-expand-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MultiSelectChipsExpandSearchComponent implements OnChanges {
  @Output() emitRemove = new EventEmitter();
  @Output() emitAdd = new EventEmitter();
  @Output() emitSearch = new EventEmitter();
  /** One more page of groups (or, ungrouped, of `filteredItems`) is wanted. Only emitted off a scroll. */
  @Output() emitLoadMore = new EventEmitter<void>();
  /** A group was opened for the first time: the parent should fetch its rows. */
  @Output() emitGroupExpand = new EventEmitter<ExpandSearchGroup>();

  /** Ungrouped options. Ignored while `grouped` is set. */
  @Input() filteredItems: any[] = [];
  @Input() displayPropertyName: string;
  @Input() placeholder: string;
  @Input() customPropertyToDisplay: string;
  /** Render `groups` instead of `filteredItems`, each one opening to reveal its own rows. */
  @Input() grouped = false;
  @Input() groups: ExpandSearchGroup[] = [];
  @Input() loading = false;
  /** More pages exist behind what is loaded — lets a scroll to the end ask for the next one. */
  @Input() hasMore = false;
  @Input() emptyLabel: string;

  @ViewChild('triggerElement', { read: ElementRef }) triggerRef: ElementRef<HTMLElement>;
  @ViewChild('showInputElement', { read: ElementRef }) inputRef: ElementRef<HTMLElement>;

  showInput = new UntypedFormControl();
  searchVisible = false;
  panelWidth = 0;
  panelMaxHeight = 0;

  // A group is expanded when the user expanded it, or when it is the only one on offer. Tracking both
  // intents separately keeps a collapse sticky: re-deriving from `groups` alone would re-expand a lone
  // group every time the parent hands down a rebuilt array.
  private expandedGroupKeys = new Set<string>();
  private collapsedGroupKeys = new Set<string>();

  // A scroll gesture fires dozens of events and `loading` only reaches us on the next change detection
  // run, so the request in flight is tracked here as well to keep one gesture to one page.
  private loadMorePending = false;

  /** Floor for the panel, so a very short window still gets a usable dropdown rather than a sliver. */
  readonly MIN_PANEL_HEIGHT = 120;
  readonly VIEWPORT_MARGIN = 12;
  /** How close to the end of the list counts as having reached it. */
  readonly SCROLL_END_THRESHOLD = 48;

  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    // The page landed (or failed): the parent is free to be asked again.
    if (changes.filteredItems || changes.groups || changes.loading?.currentValue === false) {
      this.loadMorePending = false;
    }

    if (changes.groups) {
      this.loadLoneGroup();
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.searchVisible) {
      this.measurePanel();
    }
  }

  removeItem(item) {
    this.emitRemove.emit(item);
  }

  addItem(item) {
    this.emitAdd.emit(item);
  }

  searchShows(value) {
    this.emitSearch.emit(value);
  }

  toggleSearchVisibility() {
    if (this.searchVisible) {
      this.close();
      return;
    }

    this.measurePanel();
    this.searchVisible = true;
    setTimeout(() => {
      this.inputRef?.nativeElement.focus();
    }, 0);
  }

  close() {
    this.searchVisible = false;
    this.loadMorePending = false;
  }

  onOverlayKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  toggleSelection(selected: boolean, item) {
    if (selected) {
      this.addItem(item);
    } else {
      this.removeItem(item);
    }
  }

  isGroupExpanded(group: ExpandSearchGroup): boolean {
    if (this.collapsedGroupKeys.has(group.key)) {
      return false;
    }
    return this.expandedGroupKeys.has(group.key) || this.groups.length === 1;
  }

  toggleGroup(group: ExpandSearchGroup) {
    if (this.isGroupExpanded(group)) {
      this.expandedGroupKeys.delete(group.key);
      this.collapsedGroupKeys.add(group.key);
      return;
    }

    this.collapsedGroupKeys.delete(group.key);
    this.expandedGroupKeys.add(group.key);
    this.requestGroupRows(group);
  }

  trackByGroupKey(index: number, group: ExpandSearchGroup): string {
    return group.key;
  }

  onOptionsScroll(event: Event) {
    const list = event.target as HTMLElement;
    if (list.scrollHeight - list.scrollTop - list.clientHeight <= this.SCROLL_END_THRESHOLD) {
      this.loadMore();
    }
  }

  /**
   * Deliberately never called from a lifecycle hook: a page that arrives must not be able to ask for the
   * next one, or a list whose rows are shorter than the panel pages itself to the end in a burst of
   * requests. A scroll asks for exactly one page.
   */
  loadMore() {
    if (this.loadMorePending || this.loading || !this.hasMore) {
      return;
    }

    this.loadMorePending = true;
    this.emitLoadMore.emit();
  }

  /** A lone group renders expanded, so its rows have to be fetched without waiting for a click. */
  private loadLoneGroup() {
    if (!this.grouped || this.groups?.length !== 1) {
      return;
    }

    // Not if the user has closed it: the parent rebuilds this array on every pick, and re-fetching a
    // group they deliberately collapsed would fight them.
    if (!this.isGroupExpanded(this.groups[0])) {
      return;
    }

    this.requestGroupRows(this.groups[0]);
  }

  private requestGroupRows(group: ExpandSearchGroup) {
    if (!this.grouped || group.loaded || group.loading) {
      return;
    }

    this.emitGroupExpand.emit(group);
  }

  /**
   * Caps the panel to the roomier side of the trigger. The filter bar this sits in is
   * `position: sticky`, so a panel taller than the space left on screen used to hang off the bottom
   * with nothing able to scroll it into view.
   *
   * Measuring before the overlay opens is what makes the two positions enough on their own: the panel is
   * already no taller than the better side, so CDK finds a position it fits in completely and never has
   * to fall back to pushing an over-tall panel on screen.
   */
  private measurePanel(): void {
    const trigger = this.triggerRef?.nativeElement;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - this.VIEWPORT_MARGIN;
    const spaceAbove = rect.top - this.VIEWPORT_MARGIN;

    this.panelWidth = trigger.offsetWidth;
    this.panelMaxHeight = Math.max(this.MIN_PANEL_HEIGHT, spaceBelow, spaceAbove);
  }
}
